import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import { FaWhatsapp } from 'react-icons/fa';
import { i18n } from '../../translate/i18n';
import { formatPhoneBR } from '../../utils/phoneBR';
import { safeExternalUrl } from '../../utils/safeExternalUrl';
import { DEFAULT_KNOWLEDGE_BASE_URL } from '../../constants/knowledgeBase';

const useStyles = makeStyles(() => ({
  header: {
    paddingLeft: 2,
    marginBottom: 12,
  },
  title: {
    fontWeight: 700,
    fontSize: 14.5,
    color: '#111827',
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
    lineHeight: 1.4,
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  item: {
    borderRadius: 10,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    '&:hover': { filter: 'brightness(0.97)' },
    '&:focus-visible': { outline: '2px solid #2563eb', outlineOffset: 2 },
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
  },
  itemTitle: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
  },
  itemHint: {
    fontSize: 11.5,
    lineHeight: 1.4,
  },
  whatsapp: { backgroundColor: '#f0fdf4' },
  whatsappIcon: { backgroundColor: '#22c55e' },
  whatsappTitle: { color: '#15803d' },
  whatsappHint: { color: '#16a34a', fontVariantNumeric: 'tabular-nums' },
  knowledgeBase: { backgroundColor: '#eff6ff' },
  knowledgeBaseIcon: { backgroundColor: '#3b82f6' },
  knowledgeBaseTitle: { color: '#1d4ed8' },
  knowledgeBaseHint: { color: '#2563eb' },
}));

/**
 * Conteúdo do menu de ajuda da barra superior: WhatsApp do suporte (com o
 * número) e a base de conhecimento. Sem número salvo, o WhatsApp some; sem
 * link salvo, vale a base universal. `onItemClick` fecha o menu ao abrir um
 * dos dois (cada um abre em aba nova).
 */
export default function SupportHelpPanel({ phone, knowledgeBaseUrl, onItemClick }) {
  const classes = useStyles();
  const digits = String(phone || '').replace(/\D/g, '');

  const items = [
    digits && {
      id: 'whatsapp',
      href: `https://wa.me/${digits}`,
      icon: <FaWhatsapp size={17} />,
      title: i18n.t('helpMenu.whatsapp'),
      hint: formatPhoneBR(digits),
    },
    {
      id: 'knowledgeBase',
      href: safeExternalUrl(knowledgeBaseUrl) || DEFAULT_KNOWLEDGE_BASE_URL,
      icon: <MenuBookIcon style={{ fontSize: 18 }} />,
      title: i18n.t('helpMenu.knowledgeBase'),
      hint: i18n.t('helpMenu.knowledgeBaseHint'),
    },
  ].filter(Boolean);

  return (
    <>
      <Box className={classes.header}>
        <Typography className={classes.title}>{i18n.t('helpMenu.title')}</Typography>
        <Typography className={classes.subtitle}>{i18n.t('helpMenu.subtitle')}</Typography>
      </Box>
      <Box className={classes.items}>
        {items.map(({ id, href, icon, title, hint }) => (
          <a
            key={id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onItemClick}
            className={`${classes.item} ${classes[id]}`}
          >
            <span className={`${classes.icon} ${classes[`${id}Icon`]}`}>{icon}</span>
            <span>
              <Typography className={`${classes.itemTitle} ${classes[`${id}Title`]}`}>
                {title}
              </Typography>
              <Typography className={`${classes.itemHint} ${classes[`${id}Hint`]}`}>
                {hint}
              </Typography>
            </span>
          </a>
        ))}
      </Box>
    </>
  );
}
